import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  replaceSessionInvoice,
  sessionAcknowledgePayment,
  sessionLoad,
  sessionSave,
  updateSessionInvoice,
} from '@/store/slices/session';
import { trackAddPaymentInfo } from '@/utils/analytics';
import { pulsarEvent } from '@/utils/analytics/pulsar';
import { isEmailValid } from '@/utils/strings';
import {
  STRIPE_PROVIDER,
  isInstructionRail,
  sessionConfigSignature,
  type CheckoutProvider,
} from '@/utils/session-invoice';
import type { StripeCheckoutEvent } from '@/utils/session-checkout-accounting';
import type { CardPayFn } from './CardFields';
import {
  probeCheckoutInvoice,
  reconcileCheckoutInvoice,
  type CheckoutInvoice,
} from '../../../../data/checkout-invoice';
import type { PaymentAcknowledgement } from '../../../../data/commerce-gate';
import {
  hasSessionLoadFailed,
  isCheckoutRestoring,
  isShownInvoicePaid,
  mayAutoPrepareCard,
  needsBackConfirm,
  needsSupersedeConfirm,
  otherRails,
  reissuesOnCommit,
  restorableRail,
  restoreViewFor,
  shouldRecoverCardOnEmail,
  supersedeRail,
  type CardPhase,
  type InstructionDetails,
  type CheckoutView,
} from '../../../../lib/checkout-rails';
import { useIsAlive } from '../../../../lib/liveness';
import { readStartVoucherCode } from '../../../../lib/startProperties';
import type { CheckoutStoreApi } from './store-api';
import { useCheckoutEmail } from './useCheckoutEmail';

/** Re-exported so consumers keep importing the checkout's vocabulary from the checkout. */
export type { CardPhase, CheckoutView };

export interface CardCheckoutState {
  phase: CardPhase;
  /** PaymentIntent client secret, present from `ready` onwards. */
  clientSecret: string | null;
  /** Why PREPARING the rail failed (no invoice, no secret) — distinct from a declined card,
   *  which surfaces through the shared `error` like any other submit failure. */
  error: string | null;
  /** Re-attempt preparing the card rail. */
  retry: () => void;
  /** Accept that committing to the card voids the bank coordinates already on screen, and go
   *  ahead. Only meaningful in the `confirm-supersede` phase. */
  confirmSupersede: () => void;
  /** Publish the Stripe attempt function, so the shared footer button can run it. Called by the
   *  card fields on mount, and with `null` on unmount. */
  registerPay: (pay: CardPayFn) => void;
}

/** How a Stripe attempt ended: cleared now, taken but still processing, or failed. */
export type CardCharge = 'settled' | 'accepted' | 'failed';

export interface CheckoutState {
  /**
   * The entry probe has not settled, so `method` is not yet trustworthy. The pane holds the form
   * closed on this: opening it first would flash the default rail and then swap it under the
   * buyer, and a card rail restored a beat later would look like it reset their choice.
   */
  restoring: boolean;
  /** The session is loaded but gone/never minted, so there is nothing to check out against. */
  noSession: boolean;
  /**
   * `/session/load` settled by FAILING, so there is a session id but no session data behind it.
   * Its own screen (a retryable error), never the payment form: submitting against empty data
   * would overwrite the buyer's real selection server-side and reissue their invoice against it.
   */
  loadFailed: boolean;
  /** Re-ask for the session after a failed load. */
  retryLoad: () => void;
  /** The rails on offer, in display order. The card is omitted entirely when no Stripe
   *  publishable key is configured — a payment method that can never charge is worse than none. */
  methods: CheckoutProvider[];
  /** The rail the buyer is on. */
  method: CheckoutProvider;
  /** Choose a rail. Selecting the card rail is what starts preparing it. */
  selectMethod: (method: CheckoutProvider) => void;
  /** Which SPEI screen the buyer is on. */
  view: CheckoutView;
  /** Contact email — global, shared by both rails (one order, one buyer). */
  email: string;
  setEmail: (value: string) => void;
  /** SPEI only: the name the transfer will be registered under. */
  fullName: string;
  /** Editing the name un-confirms it: what was confirmed is no longer what is in the field. */
  setFullName: (value: string) => void;
  /**
   * The buyer has committed to the payer name, so the field reads as settled and the CTA below
   * is the only thing left to do.
   *
   * Lives here rather than in the form because the form UNMOUNTS while the bank coordinates are
   * on screen; a local flag would quietly un-confirm a name the buyer had already confirmed the
   * moment they pressed Back.
   */
  nameConfirmed: boolean;
  /** Commit the payer name (the caller validates first) and persist it to the session. */
  confirmName: () => void;
  /** Reopen the name for editing. */
  editName: () => void;
  /** True while the selected rail's submit is in flight (the CTA's busy state). */
  submitting: boolean;
  /** Human-readable failure from the last attempt on the selected rail, else `null`. */
  error: string | null;
  /** Instruction details (SPEI coordinates or PIX code), present once `view === 'instructions'`. */
  details: InstructionDetails | null;
  /**
   * TEST-ONLY: the commerce-gate `test_finalize_payment_url` for the invoice currently on the
   * instructions screen, on a channel SEPARATE from {@link details}. Non-null only in a
   * non-production environment when the backend attached one; drives the QA "simulate settlement"
   * affordance and is `null` in production, so it can never surface for a real buyer.
   */
  qaFinalizeUrl: string | null;
  /** SPEI: create the invoice and advance to the instructions. */
  submit: (email: string, fullName: string) => void;
  /**
   * Card: run one payment attempt through Stripe. Resolves `'settled'` when the charge CLEARED
   * and `'accepted'` when Stripe took it but it is still processing; on both the caller can
   * acknowledge it and show the confirmation. The settlement travels in the resolved value on
   * purpose: the caller's continuation closes over the render it was created in, where
   * `cardSettled` was still false.
   *
   * Never rejects, and on a clean charge deliberately leaves the CTA busy: releasing it before
   * the screen swaps would let a buyer submit a second charge.
   */
  payCard: () => Promise<CardCharge>;
  /**
   * Return to the form (the instructions screen's Back). Called from every Back door, not only the
   * instructions screen: the desktop pane header (`CheckoutPane`) and the mobile flows' header /
   * sheet close. On the PIX rail it does NOT leave: it enters {@link backConfirmPending} instead,
   * because leaving abandons a code the buyer may already have scanned.
   */
  backToForm: () => void;
  /**
   * PIX: Back was pressed on the instructions screen and is waiting for the buyer to confirm.
   * Hook state rather than a flag inside the PIX screen, so every caller of {@link backToForm} gets
   * the confirmation without changing (a screen-local confirm would be bypassed by the header and
   * sheet Back doors).
   */
  backConfirmPending: boolean;
  /** Accept leaving the PIX instructions: back to the form (the next Continue mints a new code). */
  confirmBack: () => void;
  /** Stay on the PIX instructions. */
  dismissBack: () => void;
  /**
   * The invoice whose Pix code is on screen has been reported `paid` (websocket); the body
   * advances through `onPaid(false)` exactly as if the buyer had pressed "I've made the payment".
   * Always false on SPEI and on the form.
   */
  paidWhileShown: boolean;
  card: CardCheckoutState;
  /**
   * There is nothing left to collect: a voucher covered the whole bill, or the money had already
   * arrived. The step watches this and shows its confirmation instead of asking for a payment
   * that would be a second one.
   */
  nothingToPay: boolean;
  /**
   * A card charge CLEARED in this browser — settlement evidence the session status has not caught
   * up with yet, so the confirmation can read "paid" straight away instead of showing the bank
   * transfer's "we'll confirm your payment" copy to someone whose card just went through.
   *
   * Deliberately false for a `processing` intent. Some methods Stripe offers against a Mexican
   * PaymentIntent settle asynchronously, exactly like a bank transfer; accepting one is not the
   * money arriving, and claiming otherwise is the same bug this flow already fixed once on the
   * SPEI rail.
   */
  cardSettled: boolean;
  /** True while the "I've made the transfer" / post-charge acknowledgement is in flight. */
  acknowledging: boolean;
  /**
   * Tell the backend the payment has been made, then hand back what it says should happen next.
   * Resolves to `null` if the call fails, so the caller can still let the buyer through to the
   * confirmation rather than trapping them behind a button that never works.
   */
  acknowledge: () => Promise<PaymentAcknowledgement | null>;
}

/**
 * Drives the checkout across the card rail and the instruction rails (SPEI via commerce-gate, PIX
 * via gate2way). Shared by the desktop pane and the mobile sheet so the two behave identically.
 * A PIX code is never reused: every Continue mints a fresh one, and re-entry on gate2way restores
 * the rail only, on the form, so an old QR is never shown again.
 *
 * ── Why a session can end up holding the wrong invoice ────────────────────────────────────
 * A session holds at most ONE open invoice, and it is minted on one rail. The buyer can switch
 * rails, walk back into the wizard, change their pack and return — so the invoice that exists
 * when they commit is routinely not the one they need. The rules for detecting that, and the
 * read-before-write reconciliation that fixes it, live in `data/checkout-invoice`; this hook owns
 * WHEN they run:
 *
 * - **On entry**, once per session: a single READ. It restores the rail the buyer was on and, if
 *   they had already been given SPEI bank coordinates, the screen showing them. Nothing is created.
 * - **On committing to a rail**: selecting the card (Stripe cannot render a card form without a
 *   client secret) or submitting an instruction rail's form. Only here may an invoice be cancelled or minted.
 * - **Never on a mere toggle.** Results are memoised per rail × selection and de-duplicated
 *   while in flight, so flipping the accordion back and forth costs nothing, and re-entering an
 *   unchanged checkout costs at most the entry read.
 *
 * Every write also lands on the SESSION (`payment_method`, `spei_checkout`, `stripe_checkout`),
 * so an abandoned payment is legible server-side rather than only in the analytics stream.
 *
 * The flow-store coupling is INJECTED (`opts.store`) rather than read from a context, so base
 * v0/v3 can drive the same hook cross-flow with their own bindings; the rails on offer are passed
 * in too (`opts.methods`, ordered), so no market's rail policy is hardcoded in here.
 */
export interface UseCheckoutOptions {
  /** Pre-fill for the email field (session/account address). */
  initialEmail: string;
  /** GROSS order total, for the AddPaymentInfo conversion. */
  grossAmount: number;
  /** Currency of `grossAmount`. */
  currency: string;
  /** The rails on offer, in display order. The first is the default; the card rail is present
   *  only where Stripe can actually charge. Never hardcoded here; supplied by the call site. */
  methods: readonly CheckoutProvider[];
  /** The flow store's checkout callbacks. */
  store: CheckoutStoreApi;
}

export function useCheckout(opts: UseCheckoutOptions): CheckoutState {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { grossAmount, currency } = opts;
  const { recordCheckout, recordCardCheckout, setPaymentMethod, flushCheckout } = opts.store;
  const [email, setEmail] = useCheckoutEmail(opts.initialEmail);
  const [fullName, setFullNameState] = React.useState('');
  const [nameConfirmed, setNameConfirmed] = React.useState(false);
  const [view, setView] = React.useState<CheckoutView>('form');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [details, setDetails] = React.useState<InstructionDetails | null>(null);
  // TEST-ONLY QA settlement URL, moved in lockstep with `details` so it can only ever describe
  // the invoice currently on the instructions screen. Cleared on a rail supersede alongside the
  // coordinates, so a dead invoice's back-door never outlives the transfer it belonged to.
  const [qaFinalizeUrl, setQaFinalizeUrl] = React.useState<string | null>(null);
  const [nothingToPay, setNothingToPay] = React.useState(false);
  const [cardSettled, setCardSettled] = React.useState(false);

  // The rails on offer come from the call site (which keys the card rail on whether Stripe can
  // actually charge). `cardAvailable` stays derived from them, so the entry-probe refs below that
  // gate on it keep working unchanged.
  const methods: CheckoutProvider[] = [...opts.methods];
  const cardAvailable = opts.methods.includes(STRIPE_PROVIDER);
  // Latest offered rails, read by callbacks that must not re-memoise on them.
  const methodsRef = React.useRef(opts.methods);
  methodsRef.current = opts.methods;

  // The first offered rail is the default; no rail is hardcoded as one. The entry probe overrides
  // it when the session shows the buyer already committed to another.
  const [method, setMethod] = React.useState<CheckoutProvider>(opts.methods[0] ?? STRIPE_PROVIDER);
  const methodRef = React.useRef(method);
  methodRef.current = method;
  const [cardPhase, setCardPhase] = React.useState<CardPhase>('idle');
  const [clientSecret, setClientSecret] = React.useState<string | null>(null);
  const [cardError, setCardError] = React.useState<string | null>(null);

  const sessionId = useAppSelector((s) => s.session.sessionId);
  const sessionLoaded = useAppSelector((s) => s.session.loaded);
  const sessionLoading = useAppSelector((s) => s.session.loading);
  const sessionError = useAppSelector((s) => s.session.error);
  // The already-paid check (`useResumePaidSession` dispatches it at the flow root). `settled` is
  // set on both the fulfilled and rejected paths, so this always reaches an answer.
  const invoiceStatus = useAppSelector((s) => s.session.invoiceStatus);
  const invoiceStatusSettled = useAppSelector((s) => s.session.invoiceStatusSettled);
  // The invoice the session tracks (the load envelope's `invoice_id`, then whatever the checkout
  // registered). Read inside `ensureInvoice` as the supersede fallback.
  const sessionInvoiceId = useAppSelector((s) => s.session.invoiceId);
  const sessionInvoiceIdRef = React.useRef(sessionInvoiceId);
  sessionInvoiceIdRef.current = sessionInvoiceId;
  /** The id of the invoice whose artifact is on screen; on gate2way it is what the next Continue
   *  supersedes, and what auto-advance compares against. */
  const [shownInvoiceId, setShownInvoiceId] = React.useState<string | null>(null);
  const shownInvoiceIdRef = React.useRef(shownInvoiceId);
  shownInvoiceIdRef.current = shownInvoiceId;
  /** PIX Back is waiting for the buyer's confirmation. See `CheckoutState.backConfirmPending`. */
  const [backConfirmPending, setBackConfirmPending] = React.useState(false);
  // Read inside `ensureInvoice` (a stable callback) rather than closed over, so the money-path
  // gate always sees the CURRENT load state.
  const sessionLoadedRef = React.useRef(sessionLoaded);
  sessionLoadedRef.current = sessionLoaded;
  const sessionData = useAppSelector((s) => s.session.data);
  // Read the latest session body inside async work so a save never persists stale data and the
  // callbacks don't change identity on every unrelated session update.
  const dataRef = React.useRef(sessionData);
  dataRef.current = sessionData;
  // Everything that DETERMINES the invoice amount, as one comparable key. It is the memo key
  // below: change the pack or a feature tier and the cached invoice stops being reusable, which
  // is exactly when a reconcile has to run again.
  //
  // A marketing-site hand-off voucher (a standalone `?voucher_code=` URL param, or the one embedded
  // in `startProperties`) SUPERSEDES the session's persisted voucher (abandoned-cart, mirrors
  // buildConfigurationData). The signature — and the entry-probe below — use the EFFECTIVE config
  // (session config with that override applied) so both account for a voucher the session has not
  // been saved with yet. Keying on the raw session voucher instead let the entry-probe seed the
  // stale pre-override invoice under a key the commit then hit, bypassing the override save in
  // `ensureInvoice` and charging the old discount.
  const handoffVoucher = readStartVoucherCode();
  const effectiveConfig =
    handoffVoucher && handoffVoucher !== (sessionData.configuration_data?.voucher_code ?? null)
      ? { ...sessionData.configuration_data, voucher_code: handoffVoucher }
      : sessionData.configuration_data;
  const effectiveConfigRef = React.useRef(effectiveConfig);
  effectiveConfigRef.current = effectiveConfig;
  const configSignature = sessionConfigSignature(effectiveConfig);
  const configSignatureRef = React.useRef(configSignature);
  configSignatureRef.current = configSignature;

  // Guards async continuations so a resolving request can't setState into a dead component.
  // Uses `useIsAlive` rather than a hand-rolled "gone" ref: a ref set ONLY by the effect
  // cleanup stays set after Fast Refresh or StrictMode re-runs effects, which left this
  // checkout permanently stuck in its busy state with no `/session/invoice` call. See
  // `lib/liveness`.
  const isAlive = useIsAlive();
  // Conversion fires once per mount, shared by both rails: a buyer who enters a card, gives up
  // and switches to a transfer has supplied payment information once, not twice.
  const paymentInfoTrackedRef = React.useRef(false);
  const trackPaymentInfo = React.useCallback((): void => {
    if (paymentInfoTrackedRef.current) return;
    paymentInfoTrackedRef.current = true;
    trackAddPaymentInfo({ value: grossAmount, currency });
    pulsarEvent('add_payment_info', { value: grossAmount, currency });
  }, [grossAmount, currency]);

  /* ── invoice memo + single flight ────────────────────────────────────────────────────── */

  // Resolved invoices, keyed by rail × selection. A key that is already here needs no request at
  // all, which is what makes toggling the accordion free; a selection change mints a new key and
  // so forces the reconcile that catches the stale invoice.
  const invoiceCacheRef = React.useRef(new Map<string, CheckoutInvoice>());
  // Reconciles currently in flight, same key. Two callers asking at once (a re-render, a double
  // click) share ONE request rather than racing two cancel/reissue cycles at each other.
  const inflightRef = React.useRef(new Map<string, Promise<CheckoutInvoice>>());

  const invoiceKey = React.useCallback(
    (provider: CheckoutProvider): string => `${provider}|${configSignatureRef.current}`,
    [],
  );

  // Mirror of everything tied to the session's ONE invoice, kept in a ref so the callback below
  // can read the latest values without taking them as dependencies (the established pattern in
  // this hook — see `dataRef`).
  /** The buyer picked a rail in this visit, rather than being restored onto one. */
  const chosenExplicitlyRef = React.useRef(false);
  /** Bumped by every supersede. A reconcile that started before the bump must not write its
   *  result anywhere afterwards: the invoice it describes has since been handed to the other
   *  rail, so caching or painting it re-surfaces a dead CLABE. */
  const supersedeSeqRef = React.useRef(0);
  const detailsRef = React.useRef(details);
  detailsRef.current = details;
  const artifactsRef = React.useRef({ cardPhase, clientSecret, details, view });
  artifactsRef.current = { cardPhase, clientSecret, details, view };

  /**
   * Forget the rail that a commit to `committed` has just superseded.
   *
   * Called BEFORE every commit attempt, because `reconcileCheckoutInvoice` cancels the other
   * rail's invoice and then mints — so it can void that invoice and still throw, and artifacts
   * kept past that point are dead ones the buyer can act on.
   *
   * **Touches only the SUPERSEDED rail's fields.** It used to write all four back from
   * `artifactsRef` (the last RENDERED values), which was harmless while it ran in a later tick but
   * silently clobbers the caller's own `setCardPhase('preparing')` once it runs in the same batch.
   * Writing only what the superseded rail owns makes that impossible by construction rather than
   * by call ordering. The rule is still `lib/checkout-rails.supersedeRail`, pure and unit-tested.
   *
   * Bumps a supersede sequence so an already-in-flight reconcile for the rail being dropped cannot
   * re-insert its result into the memo (or paint it) after the fact.
   */
  const supersedeOtherRail = React.useCallback(
    (committed: CheckoutProvider): void => {
      for (const rail of otherRails(committed, methodsRef.current)) invoiceCacheRef.current.delete(invoiceKey(rail));
      supersedeSeqRef.current += 1;
      const next = supersedeRail(artifactsRef.current, committed);
      setBackConfirmPending(false);
      if (!isInstructionRail(committed)) {
        // Only the bank transfer's artifacts are dead. The card's own phase belongs to the caller
        // that is mid-commit; writing it here is what clobbered `preparing`.
        setDetails(next.details);
        // The QA URL belongs to the coordinates being dropped; clear it with them so the
        // superseded invoice's back-door cannot outlive the transfer.
        setQaFinalizeUrl(null);
        setShownInvoiceId(null);
        setView(next.view);
        return;
      }
      setClientSecret(next.clientSecret);
      setCardPhase(next.cardPhase);
      // An instruction rail superseding the OTHER instruction rail: that rail's details are dead
      // too, and so is the QA URL that belonged to them.
      if (next.details == null && artifactsRef.current.details != null) {
        setDetails(next.details);
        setQaFinalizeUrl(null);
        setShownInvoiceId(null);
        setView(next.view);
      }
      // A dropped secret must also drop the selection it was minted for, or the staleness guard
      // would think the rail is still prepared for the current selection.
      if (next.clientSecret == null) preparedSignatureRef.current = null;
    },
    [invoiceKey],
  );

  /**
   * Persist the buyer's email onto the SESSION, then reconcile the invoice for `provider`.
   *
   * The email save is not optional and not merely tidy: `/invoice` reads the buyer's email OFF
   * the session and 500s `SESSION_NO_EMAIL` without it, and the association is volatile — a prior
   * autosave may have dropped it. So it is committed and awaited before the invoice is asked for,
   * on both rails.
   *
   * The payer NAME is deliberately not part of the memo key. Correcting it must not cancel and
   * reissue: that would void a CLABE the buyer may already have pasted into their banking app,
   * to change a field the transfer is matched on by reference anyway. The corrected name is still
   * recorded on the session.
   */
  const ensureInvoice = React.useCallback(
    (provider: CheckoutProvider, payerName: string, buyerEmail: string): Promise<CheckoutInvoice> => {
      const id = sessionId;
      if (!id) return Promise.reject(new Error('no session'));
      // The wizard gates every OTHER `sessionSave` on `loaded` because `POST /session/save`
      // overwrites `data` wholesale: saving from an unloaded session replaces the buyer's real
      // campaign and features with whatever this checkout holds, and the invoice is then
      // reconciled against that emptied selection. This is the one save on the money path, so it
      // carries the same gate rather than trusting callers to be unreachable.
      if (!sessionLoadedRef.current) return Promise.reject(new Error('session not loaded'));
      const key = invoiceKey(provider);

      const cached = invoiceCacheRef.current.get(key);
      if (cached) return Promise.resolve(cached);
      const inflight = inflightRef.current.get(key);
      if (inflight) return inflight;

      // Stamped at the START. A supersede that lands while this is in flight means the invoice
      // being fetched has since been handed to the other rail, so its result must not be cached
      // or painted afterwards — that is how a cancelled CLABE got back onto the screen.
      const seqAtStart = supersedeSeqRef.current;
      const run = (async (): Promise<CheckoutInvoice> => {
        // Persist the buyer email and the effective (hand-off-overridden) voucher onto the session
        // BEFORE the invoice is priced from it: `/invoice` reads the email off the session body and
        // 500s (SESSION_NO_EMAIL) without it, and derives the discount from the saved
        // `configuration_data`, not the redemption param. `effectiveConfigRef` already carries the
        // voucher override (see the signature note above); this is the one save on the money path,
        // gated by `sessionLoadedRef` above.
        const nextConfig = { ...effectiveConfigRef.current, ...(buyerEmail ? { emailAddress: buyerEmail } : {}) };
        await dispatch(sessionSave({ id, data: { ...dataRef.current, configuration_data: nextConfig } })).unwrap();
        return reconcileCheckoutInvoice({
          sessionId: id,
          provider,
          config: nextConfig,
          fullName: payerName,
          voucherCode: nextConfig.voucher_code ?? null,
          // What a gate2way POST supersedes: the code on screen, else the session's tracked invoice
          // (re-entry: form view, nothing shown yet, and the load envelope's `invoice_id` is the
          // newest). Never the GET read (Q7): it is `findFirst` without `orderBy`.
          supersedesInvoiceId: reissuesOnCommit(provider)
            ? (shownInvoiceIdRef.current ?? sessionInvoiceIdRef.current)
            : null,
        });
      })();

      inflightRef.current.set(key, run);
      return run
        .then((result) => {
          if (supersedeSeqRef.current === seqAtStart) invoiceCacheRef.current.set(key, result);
          return result;
        })
        .finally(() => {
          inflightRef.current.delete(key);
        });
    },
    [sessionId, dispatch, invoiceKey],
  );

  /* ── card rail ───────────────────────────────────────────────────────────────────────── */

  const recordCard = React.useCallback(
    (event: StripeCheckoutEvent): void => recordCardCheckout(event),
    [recordCardCheckout],
  );

  // The selection the current client secret was minted for. Watched below, because a secret is
  // tied to an amount and an amount is only correct for one selection.
  const preparedSignatureRef = React.useRef<string | null>(null);
  // The rail failed for the ONE reason the buyer can fix by typing. Tracked separately from the
  // error phase so the recovery below can be scoped to it.
  const blockedOnEmailRef = React.useRef(false);

  /** Get the card rail chargeable: reconcile the invoice, then hand Stripe its client secret. */
  const prepareCard = React.useCallback((opts?: { authorised?: boolean }): void => {
    if (!sessionId) return;
    // THE gate, not one of several. Every door into the card rail comes through here — selecting
    // it, restoring onto it, the email recovery, "Try again" — and each one commits, so each one
    // would void bank coordinates the buyer may be mid-transfer against. Gating at a single call
    // site is what let the recovery effect and the retry bypass it.
    if (!opts?.authorised && needsSupersedeConfirm(STRIPE_PROVIDER, { details: detailsRef.current })) {
      setCardPhase('confirm-supersede');
      return;
    }
    // The invoice is billed to the address on the session, so a card checkout cannot start
    // without a usable one. Surfaced as the rail's own error rather than a silent no-op.
    const buyerEmail = email.trim();
    if (!isEmailValid(buyerEmail)) {
      blockedOnEmailRef.current = true;
      setCardPhase('error');
      setCardError(t('Enter a valid email address to pay by card.'));
      return;
    }
    blockedOnEmailRef.current = false;
    setCardPhase('preparing');
    setCardError(null);
    // BEFORE the attempt, not after it. `reconcileCheckoutInvoice` cancels the other rail's
    // invoice and then mints, so it can void the bank transfer and STILL throw (the reissue
    // deadline expires, the new row carries no client secret, the network drops). Superseding only
    // on the success path left the voided invoice's CLABE memoised and on screen, and switching
    // back replayed it from cache with no request at all — a buyer transferring to it sends real
    // money to an invoice that will never be reconciled. Dropping it up front is recoverable (the
    // next commit to that rail simply reconciles again); leaving it is not.
    supersedeOtherRail(STRIPE_PROVIDER);
    const seqAtPrepare = supersedeSeqRef.current;
    void (async () => {
      try {
        // No payer name on this rail: it is the SPEI provider that registers the transfer under
        // one, and the backend ignores it for Stripe.
        const invoice = await ensureInvoice(STRIPE_PROVIDER, '', buyerEmail);
        if (!isAlive()) return;
        if (invoice.paid) {
          // A voucher covered the whole bill (or the money was already in): there is nothing to
          // charge, so never mount a card form for it.
          setNothingToPay(true);
          return;
        }
        // Superseded mid-flight: this secret points at an invoice the other rail has taken.
        if (supersedeSeqRef.current !== seqAtPrepare) return;
        setClientSecret(invoice.clientSecret);
        preparedSignatureRef.current = configSignatureRef.current;
        setCardPhase('ready');
        recordCard({ step: 'ready' });
      } catch (err) {
        if (!isAlive()) return;
        setCardPhase('error');
        setCardError(t("We couldn't start the card payment. Check your connection and try again."));
        // Recorded on the session too: a rail that never became chargeable is a real reason a
        // buyer abandoned, and it is invisible in Stripe's own dashboard (nothing reached it).
        recordCard({ error: { type: 'invoice_error', message: String(err) } });
      }
    })();
  }, [sessionId, email, ensureInvoice, isAlive, recordCard, supersedeOtherRail, t]);

  const retryCard = React.useCallback((): void => {
    // Drop the memoised failure path so the retry really re-asks (a transient network error must
    // not be cached as "this rail is broken").
    invoiceCacheRef.current.delete(invoiceKey(STRIPE_PROVIDER));
    prepareCard();  // unauthorised on purpose: a retry re-asks if coordinates are live again
  }, [invoiceKey, prepareCard]);

  // The rail stopped because the email wasn't usable yet. Once it is, recover on its own rather
  // than making the buyer hunt for a retry button over a field they have just fixed. Scoped to
  // THAT failure (an invoice or network error must not re-fire on every keystroke) AND to the card
  // rail: recovering is a commit, and committing supersedes the bank transfer's invoice. See
  // `lib/checkout-rails.shouldRecoverCardOnEmail`.
  React.useEffect(() => {
    if (!shouldRecoverCardOnEmail(method, blockedOnEmailRef.current, isEmailValid(email.trim()))) return;
    prepareCard();
  }, [email, method, prepareCard]);

  // The Stripe attempt, published by the card fields while they are mounted. Held in a ref
  // rather than state: it is an imperative handle, and re-rendering the whole checkout every
  // time Stripe re-memoises it would be pure noise.
  const payRef = React.useRef<CardPayFn>(null);
  const registerPay = React.useCallback((fn: CardPayFn): void => {
    payRef.current = fn;
  }, []);

  const payCard = React.useCallback(async (): Promise<CardCharge> => {
    const run = payRef.current;
    if (!run) return 'failed';
    setSubmitting(true);
    setError(null);
    recordCard({ step: 'submitted', attempt: true });

    const outcome = await run();

    // Entering card details is worth recording whatever happened next: it is what separates a
    // decline from an abandoned empty form.
    if (outcome.paymentInfoEntered) {
      recordCard({ paymentInfo: true });
      trackPaymentInfo();
    }

    if (!outcome.ok) {
      if (isAlive()) {
        setError(outcome.error?.message ?? t('Payment failed. Please try again.'));
        setSubmitting(false);
      }
      // The buyer's failure lands on the SESSION, not only in Stripe's dashboard: a decline is
      // the commonest reason a paid-looking funnel has no money in it, and the issuer's
      // `decline_code` is what tells support whether to suggest another card.
      recordCard({ status: 'failed', error: outcome.error });
      return 'failed';
    }

    const status = outcome.status ?? 'succeeded';
    recordCard({ status });
    // Settlement evidence ONLY for a charge that actually cleared; a `processing` intent has been
    // accepted, not paid, and the confirmation must say so.
    const settled = status === 'succeeded';
    if (settled && isAlive()) setCardSettled(true);
    // `submitting` stays TRUE on purpose: the CTA must not come back to life between the charge
    // clearing and the confirmation replacing this screen.
    return settled ? 'settled' : 'accepted';
  }, [recordCard, trackPaymentInfo, isAlive, t]);

  /* ── entry restore ───────────────────────────────────────────────────────────────────── */

  // Where the buyer left off, read once per session. A READ, never a write: a POST here would
  // supersede the invoice and void coordinates that may already be in someone's banking app.
  const probedSessionRef = React.useRef<string | null>(null);
  const [probeSettled, setProbeSettled] = React.useState(false);
  React.useEffect(() => {
    if (!sessionLoaded || !sessionId || probedSessionRef.current === sessionId) return;
    probedSessionRef.current = sessionId;
    setProbeSettled(false);
    void (async () => {
      try {
        const probe = await probeCheckoutInvoice(sessionId, effectiveConfigRef.current);
        if (!isAlive()) return;
        if (probe?.paid) {
          // Settled before this checkout even opened. `useResumePaidSession` is what acknowledges
          // and routes it; all this has to do is not ask for a second payment.
          setNothingToPay(true);
        } else if (probe?.provider && methodsRef.current.includes(probe.provider)) {
          // Only a rail the picker actually offers: a probe must never select one it does not.
          setMethod(probe.provider);
          if (probe.usable) {
            // Reusable exactly as it stands: seed the memo so committing to that rail costs no
            // request at all. Never for a rail that reissues on commit (PIX): its Continue must
            // always mint, so a seeded entry would only be deleted again.
            if (!reissuesOnCommit(probe.provider)) invoiceCacheRef.current.set(invoiceKey(probe.provider), probe.usable);
            // The buyer already HAS bank coordinates. Put them back on the screen that shows
            // them, rather than on a form that would look like their transfer never happened.
            // PIX always restores to the form (see `restoreViewFor`).
            const reachedInstructions = Boolean(
              dataRef.current.configuration_data?.spei_checkout?.reached?.instructions,
            );
            const restoredView = restoreViewFor({
              provider: probe.provider,
              hasDetails: probe.usable.details != null,
              reachedInstructions,
            });
            if (restoredView === 'instructions') {
              setDetails(probe.usable.details);
              setQaFinalizeUrl(probe.usable.qaFinalizeUrl);
              setShownInvoiceId(probe.usable.invoiceId);
              setView('instructions');
              // Register the restored invoice on the SESSION too (adopt-on-null; a non-null
              // `invoiceId` the envelope already set wins, since `updateSessionInvoice` refuses a
              // conflicting change). Keeps the QA settlement refetch attributable on a resumed
              // session, matching the fresh-mint path in `submit`.
              dispatch(updateSessionInvoice({ invoice_id: probe.usable.invoiceId }));
            }
          }
        } else {
          // No invoice to read the rail off — fall back to what the buyer last selected, if the
          // session remembers it.
          const saved = restorableRail(
            dataRef.current.configuration_data?.payment_method,
            methodsRef.current,
          );
          if (saved && (saved !== STRIPE_PROVIDER || cardAvailable)) setMethod(saved);
        }
      } finally {
        if (isAlive()) setProbeSettled(true);
      }
    })();
  }, [sessionLoaded, sessionId, cardAvailable, invoiceKey, isAlive, dispatch]);

  // Always reaches a decision — the pane holds the form closed on this, and a `restoring` that
  // cannot clear is a checkout nobody can pay from. The rule (including the failed-load deadlock
  // it exists to avoid) lives in `lib/checkout-rails.isCheckoutRestoring`, pure and tested.
  const sessionSettled = sessionLoaded || (sessionError != null && !sessionLoading);
  const restoring = isCheckoutRestoring({
    sessionSettled,
    sessionLoaded,
    hasSession: sessionId != null,
    probeSettled,
  });
  // Settled by failing: there IS a session id but no session data behind it. Its own screen, never
  // the payment form — see `hasSessionLoadFailed`.
  const loadFailed = hasSessionLoadFailed({ sessionSettled, sessionLoaded });

  /** Re-ask for the session a failed load left us without. Usually transient (a 5xx, an offline
   *  blip), so the error screen offers this rather than dead-ending the buyer. */
  const retryLoad = React.useCallback((): void => {
    if (!sessionId) return;
    void dispatch(sessionLoad({ id: sessionId }));
  }, [dispatch, sessionId]);

  // A client secret is tied to an AMOUNT, and the amount is only right for the selection the
  // invoice was minted against. If the order is re-priced while the rail is prepared — a resumed
  // session being switched to MXN, a voucher landing, a pack change that did not unmount this
  // screen — the held secret would charge the old total. Drop it and fall back to `idle`, which
  // re-runs the prepare above; the reconcile then sees the stale invoice and reissues it.
  React.useEffect(() => {
    if (preparedSignatureRef.current == null || preparedSignatureRef.current === configSignature) return;
    preparedSignatureRef.current = null;
    setClientSecret(null);
    setCardPhase('idle');
  }, [configSignature]);

  // Once the rail is known, get the card ready if that is where the buyer is. Restoring INTO the
  // card rail is the same commitment as selecting it, and the seeded memo usually makes it free.
  //
  // UNLESS starting it would void bank coordinates the buyer is already holding — then it asks
  // first, because they may be mid-transfer against them. See `needsSupersedeConfirm`.
  React.useEffect(() => {
    if (restoring || nothingToPay) return;
    if (method !== STRIPE_PROVIDER || cardPhase !== 'idle') return;
    if (
      !mayAutoPrepareCard({
        chosenExplicitly: chosenExplicitlyRef.current,
        paidCheckAnswered: invoiceStatusSettled || invoiceStatus != null,
      })
    )
      return;
    prepareCard();
  }, [restoring, nothingToPay, method, cardPhase, prepareCard, invoiceStatusSettled, invoiceStatus]);

  /** The buyer accepted that switching to the card voids the transfer they were given. */
  const confirmCardSwitch = React.useCallback((): void => {
    prepareCard({ authorised: true });
  }, [prepareCard]);

  /* ── selection ───────────────────────────────────────────────────────────────────────── */

  const selectMethod = React.useCallback(
    (next: CheckoutProvider): void => {
      if (next === method) return;
      // The buyer chose this rail in this visit, so preparing it no longer needs to wait on the
      // already-paid read (see `mayAutoPrepareCard`).
      chosenExplicitlyRef.current = true;
      setMethod(next);
      setError(null);
      // The buyer's own choice, persisted so a reopened checkout comes back to it. Debounced and
      // idempotent in the store, so this costs nothing when it isn't a change.
      setPaymentMethod(next);
      if (isInstructionRail(next)) {
        recordCheckout({ step: 'form' });
      } else {
        recordCard({ step: 'form' });
      }
    },
    [method, setPaymentMethod, recordCard, recordCheckout],
  );

  /* ── instruction rails (SPEI, PIX) ───────────────────────────────────────────────────── */

  const submit = React.useCallback(
    (submittedEmail: string, submittedName: string): void => {
      const rail = methodRef.current;
      if (!isInstructionRail(rail)) return;
      if (!sessionId) {
        setError(t('Your checkout session expired. Go back and start again.'));
        return;
      }
      setSubmitting(true);
      setError(null);
      // Account for the payer name on the SESSION the moment it is submitted — before the
      // invoice call, so it survives even if that call fails. Deliberately NOT tagged with a
      // step: arriving at the form and submitting it are different events, and counting a
      // submit as an arrival would make `reached.form` mean neither.
      recordCheckout({ fullName: submittedName });
      trackPaymentInfo();
      // Before the attempt, for the same reason as the card rail: this reconcile can cancel the
      // card's PaymentIntent and still throw, and a client secret kept past that mounts Stripe
      // Elements against a dead intent.
      //
      // No `needsSupersedeConfirm` here, by design, not a missed gate: only {PIX, card} or
      // {SPEI, card} are ever offered together, so an instruction-rail commit never voids another
      // instruction rail's details.
      supersedeOtherRail(rail);
      // A PIX Continue must never be served from the memo: every Continue mints a fresh code.
      if (reissuesOnCommit(rail)) invoiceCacheRef.current.delete(invoiceKey(rail));
      const seqAtSubmit = supersedeSeqRef.current;
      void (async () => {
        try {
          const invoice = await ensureInvoice(rail, submittedName, submittedEmail.trim());
          if (!isAlive()) return;
          if (invoice.paid) {
            // A voucher covered the whole bill: no transfer to instruct.
            setNothingToPay(true);
            return;
          }
          // Superseded while this was in flight: the card rail has taken the session's invoice
          // and cancelled this one, so these coordinates are already dead. Painting them is the
          // exact loss this guards against.
          if (supersedeSeqRef.current !== seqAtSubmit) return;
          setDetails(invoice.details);
          setQaFinalizeUrl(invoice.qaFinalizeUrl);
          setShownInvoiceId(invoice.invoiceId);
          setView('instructions');
          // Register the freshly-minted invoice on the SESSION. SPEI: adopt-on-null, exactly as the
          // `invoice_update` websocket push does. Without it `session.invoiceId` stays null on the
          // instructions screen, so the forced `sessionFetchInvoiceStatus` the QA settlement
          // affordance fires on tab-return is unattributable and its `paid` is silently dropped
          // (see `session.invoice.test.ts` → "a checkout-minted SPEI invoice must be registered").
          // PIX: the session must FOLLOW the reissue (re-point, not adopt-on-null), or the slice
          // guard drops the `paid` push for the new invoice.
          dispatch(
            reissuesOnCommit(rail)
              ? replaceSessionInvoice({ invoice_id: invoice.invoiceId })
              : updateSessionInvoice({ invoice_id: invoice.invoiceId }),
          );
          // The buyer now HAS the bank coordinates — the point the funnel cares about.
          recordCheckout({ step: 'instructions' });
        } catch {
          if (!isAlive()) return;
          setError(
            reissuesOnCommit(rail)
              ? t("We couldn't get your Pix code. Check your connection and try again.")
              : t("We couldn't get your transfer details. Check your connection and try again."),
          );
        } finally {
          if (isAlive()) setSubmitting(false);
        }
      })();
    },
    [sessionId, ensureInvoice, invoiceKey, isAlive, recordCheckout, trackPaymentInfo, supersedeOtherRail, dispatch, t],
  );

  /* ── payer name ──────────────────────────────────────────────────────────────────────── */

  const setFullName = React.useCallback((value: string): void => {
    setFullNameState(value);
    // Any edit un-confirms: the name that was committed is no longer the name in the field.
    setNameConfirmed(false);
  }, []);

  const confirmName = React.useCallback((): void => {
    setNameConfirmed(true);
    // Persisted HERE, not only at submit. A buyer who confirms and then abandons has still told
    // us who the transfer would come from, and that is the one field support needs to match a
    // stray payment back to a session.
    recordCheckout({ fullName: fullName.trim() });
  }, [fullName, recordCheckout]);

  const editName = React.useCallback((): void => setNameConfirmed(false), []);

  // Arrival at the contact form: once when the checkout opens, and again on every return from
  // the coordinates screen. Together with the `instructions` count this is what shows a buyer
  // bouncing back and forth rather than making one clean pass.
  //
  // Held until the entry restore has settled, and skipped when it put the buyer straight back on
  // their bank coordinates: recording an arrival at a form they were never shown would make the
  // counter mean nothing.
  const formArrivalRecorded = React.useRef(false);
  React.useEffect(() => {
    if (formArrivalRecorded.current || restoring || view !== 'form') return;
    formArrivalRecorded.current = true;
    if (isInstructionRail(method)) recordCheckout({ step: 'form' });
    else recordCard({ step: 'form' });
  }, [restoring, view, method, recordCheckout, recordCard]);

  const backToForm = React.useCallback(() => {
    // PIX: leaving abandons a code the buyer may already have scanned, so ask first.
    if (needsBackConfirm(methodRef.current, view)) {
      setBackConfirmPending(true);
      return;
    }
    setShownInvoiceId(null);
    setView('form');
    recordCheckout({ step: 'form' });
  }, [view, recordCheckout]);

  const confirmBack = React.useCallback((): void => {
    setBackConfirmPending(false);
    setShownInvoiceId(null);
    setView('form');
    recordCheckout({ step: 'form' });
  }, [recordCheckout]);

  const dismissBack = React.useCallback((): void => setBackConfirmPending(false), []);

  // Auto-advance (PIX only, spec 4.7), derived at render: no effect, no polling, no dispatch here;
  // the body consumes the boolean. The `invoice_update` websocket feeds `session.invoiceStatus`,
  // the slice guard drops other ids, and `replaceSessionInvoice` keeps the tracked id equal to the
  // shown one. The flows' `resume.checkedOut` path may fire too, and that is fine:
  // `sessionAcknowledgePayment`'s `condition` is the once-guard.
  const paidWhileShown = isShownInvoicePaid({
    rail: details?.rail ?? null,
    view,
    shownInvoiceId,
    sessionInvoiceId,
    invoiceStatus,
  });

  /* ── acknowledgement ─────────────────────────────────────────────────────────────────── */

  const [acknowledging, setAcknowledging] = React.useState(false);
  const acknowledge = React.useCallback(async (): Promise<PaymentAcknowledgement | null> => {
    if (!sessionId) return null;
    setAcknowledging(true);
    // Record the buyer's own confirmation regardless of what the backend answers — "said they
    // paid" is a distinct funnel state from "backend agreed", and the call below can fail.
    //
    // FLUSHED, not debounced, and awaited BEFORE the acknowledge call. `/payment-acknowledged`
    // is a real state transition: it can advance the session to `customization`, after which
    // the session no longer accepts a `configuration_data` body at all (`/session/save` →
    // INVALID_SESSION_STATE) — so a save left sitting on the 800ms debounce would fire just
    // after the boundary and be rejected, losing the final step of the funnel. This is the
    // last write of the checkout; it has to land while the session is still `configuration`.
    //
    // Every instruction rail records under `spei` (spec 4.8: no new key for PIX).
    await flushCheckout(
      isInstructionRail(methodRef.current)
        ? { spei: { step: 'acknowledged' } }
        : { stripe: { step: 'acknowledged' } },
    );
    try {
      // Through the STORE thunk, not the bare endpoint helper. The thunk owns the exactly-once
      // guard, latches the verdict durably (so a reload of the confirmation still knows whether to
      // offer onboarding), and re-reads the session so the save gates see the new status. Calling
      // the helper directly left all three undone, which let the entry recovery acknowledge the
      // same session a SECOND time whenever `can_continue` was false — the one case where the
      // session stays on `configuration`, and where a second call means a second login-link email.
      //
      // It never rejects, so a failure surfaces as a null verdict rather than trapping a buyer who
      // has already moved money on the coordinates screen; the confirmation decides its own variant.
      const { verdict } = await dispatch(sessionAcknowledgePayment({ sessionId })).unwrap();
      return verdict;
    } catch {
      return null;
    } finally {
      if (isAlive()) setAcknowledging(false);
    }
  }, [sessionId, dispatch, flushCheckout, isAlive]);

  const card = React.useMemo<CardCheckoutState>(
    () => ({
      phase: cardPhase,
      clientSecret,
      error: cardError,
      retry: retryCard,
      confirmSupersede: confirmCardSwitch,
      registerPay,
    }),
    [cardPhase, clientSecret, cardError, retryCard, confirmCardSwitch, registerPay],
  );

  return {
    restoring,
    // Only once the session has actually loaded — a null id mid-load is "not yet", not "gone".
    noSession: sessionLoaded && !sessionId,
    loadFailed,
    retryLoad,
    methods,
    method,
    selectMethod,
    view,
    email,
    setEmail,
    fullName,
    setFullName,
    nameConfirmed,
    confirmName,
    editName,
    submitting,
    error,
    details,
    qaFinalizeUrl,
    submit,
    payCard,
    backToForm,
    backConfirmPending,
    confirmBack,
    dismissBack,
    paidWhileShown,
    card,
    nothingToPay,
    cardSettled,
    acknowledging,
    acknowledge,
  };
}
